import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { cn } from '../common/utils';
import { Button } from '../components/actions/Button';
import * as Icon from '../components/icons';
import { ActionBar } from '../components/nav/ActionBar';
import { aiAgentQueries } from './store/aiAgent.state';

export type AiAgentMessageInputProps = {
  onMessageAdded: (message: string) => void;
};

const AiAgentMessageInput: React.FC<AiAgentMessageInputProps> = ({
  onMessageAdded,
}) => {
  const { t } = useTranslation('app');
  const [messageLog, setMessageLog] = useRecoilState(aiAgentQueries);
  const [text, setText] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const heightUpdateTimeoutRef = useRef<NodeJS.Timeout>();

  const isLoading = false;

  // Auto-resize the textarea as content changes
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = '0';

    // Calculate new height (clamped between min and max)
    const minHeight = 48; // 2 lines
    const maxHeight = 144; // ~6 lines
    const newHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight,
    );

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  // Update height on content change
  useEffect(() => {
    adjustTextareaHeight();
  }, [text, adjustTextareaHeight]);

  function addMessage() {
    if (_.isEmpty(text)) return;
    onMessageAdded(text);
    setText('');
    // Reset height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px';
      textareaRef.current.value = '';
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'ArrowUp' && !text) {
      const lastMessage = _.last(messageLog)?.query;
      if (!_.isEmpty(lastMessage)) {
        setText(lastMessage);
        if (textareaRef.current) {
          textareaRef.current.value = lastMessage;
          adjustTextareaHeight();
        }
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addMessage();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    setText(content);

    // Use requestAnimationFrame for smooth height updates
    requestAnimationFrame(adjustTextareaHeight);
  };

  return (
    <div className="z-menu sticky bottom-2 left-0 right-0 p-10">
      <ActionBar className="!bg-sidebar-header/70 flex flex-col rounded-2xl px-2 py-2 shadow-xl backdrop-blur-md">
        <div className="flex w-full flex-col items-center rounded-2xl p-0.5">
          <div
            className={cn(
              'flex w-full flex-col rounded-2xl p-2',
              'transition-colors duration-200',
              isFocused ? 'bg-app-input/95' : 'bg-app-input/75',
            )}
          >
            <div className="relative flex w-full flex-col">
              <textarea
                ref={textareaRef}
                rows={1}
                aria-label={t`Ask AI about the warehouse Activity`}
                placeholder={t`Ask AI about the warehouse Activity`}
                value={text}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                disabled={isLoading}
                className={cn(
                  'mt-2 max-h-[144px] min-h-[48px] w-full',
                  'rounded-xl border-none',
                  'px-3 py-2 pr-[4.5rem]',
                  'text-menu-text bg-transparent',
                  'overflow-y-auto overflow-x-hidden',
                  'focus:outline-none focus:ring-0',
                  'whitespace-pre-wrap break-words',
                  'leading-6',
                  'transition-all duration-100 ease-out',
                  'placeholder:text-menu-text/30',
                  'pb-10',
                  isLoading && 'cursor-not-allowed opacity-50',
                )}
                style={{
                  resize: 'none',
                }}
              />
              <div className="absolute bottom-0 right-2 flex items-center gap-2">
                <Button
                  label={isLoading ? t`Thinking...` : t`Ask AI`}
                  buttonType="primary"
                  onPress={() => addMessage()}
                  hasIconBefore={
                    <Icon.HelpBot className={cn('h-6 w-6 fill-current')} />
                  }
                  className={cn(
                    '!rounded-xl !py-1',
                    'transition-opacity duration-200',
                    text ? 'opacity-100' : 'opacity-75',
                  )}
                  isDisabled={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2 w-full px-4 text-start text-xs opacity-50">
          {`WareBee AI (Beta)`} {` — `}
          {t`AI can make mistakes. Check important info.`}
        </div>
      </ActionBar>
    </div>
  );
};

export default AiAgentMessageInput;
